import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";

const basePath = `${API_URL.idp}/client`;

const tenantHeaders = (tenantId, extra = {}) => ({
  headers: {
    ...(tenantId ? { tenantId: String(tenantId).trim() } : {}),
    ...extra,
  },
});

const getAllTenantClients = async (tenantId) => {
  return API.get(
    basePath,
    "tenant_clients_list",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const getTenantClientById = async (clientId, tenantId) => {
  if (!clientId) {
    throw new Error("Tenant client id is missing.");
  }

  return API.get(
    `${basePath}/${clientId}`,
    "tenant_client_by_id",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

/** Authenticated client basic config (allowSelfUserCreation, allowLoginViaOTP, privacyPolicy). */
const getTenantClientBasic = async (clientId, tenantId) => {
  if (!clientId) {
    throw new Error("Tenant client id is missing.");
  }

  return API.get(
    `${basePath}/${clientId}/basic`,
    "tenant_client_basic",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const createTenantClient = async (payload = {}, tenantId) => {
  const redirectUri = Array.isArray(payload.redirectUri)
    ? payload.redirectUri.map(String).map((uri) => uri.trim()).filter(Boolean)
    : [];

  return API.post(
    basePath,
    "tenant_client_create",
    {
      name: String(payload.name || "").trim(),
      grantType: Array.isArray(payload.grantType)
        ? payload.grantType.filter(Boolean).map(String)
        : [],
      allowUserCreation: Boolean(payload.allowUserCreation),
      allowSelfUserCreation: Boolean(payload.allowSelfUserCreation),
      allowLoginViaOTP: Boolean(payload.allowLoginViaOTP),
      redirectUri,
      ...(String(payload.privacyPolicy || "").trim()
        ? { privacyPolicy: String(payload.privacyPolicy).trim() }
        : {}),
      roles: Array.isArray(payload.roles)
        ? payload.roles.map(String).map((role) => role.trim()).filter(Boolean)
        : [],
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantClient = async (clientId, payload = {}, tenantId) => {
  if (!clientId) {
    throw new Error("Tenant client id is missing.");
  }

  const body = {};
  if (payload.name !== undefined) body.name = String(payload.name || "").trim();
  if (payload.grantType !== undefined) {
    body.grantType = Array.isArray(payload.grantType)
      ? payload.grantType.filter(Boolean).map(String)
      : [];
  }
  if (payload.redirectUri !== undefined) {
    body.redirectUri = Array.isArray(payload.redirectUri)
      ? payload.redirectUri.map(String).map((uri) => uri.trim()).filter(Boolean)
      : [];
  }
  if (payload.allowUserCreation !== undefined) {
    body.allowUserCreation = Boolean(payload.allowUserCreation);
  }
  if (payload.allowSelfUserCreation !== undefined) {
    body.allowSelfUserCreation = Boolean(payload.allowSelfUserCreation);
  }
  if (payload.allowLoginViaOTP !== undefined) {
    body.allowLoginViaOTP = Boolean(payload.allowLoginViaOTP);
  }
  // Omit empty privacyPolicy — API rejects "" as invalid URI format.
  if (payload.privacyPolicy !== undefined) {
    const privacyPolicy = String(payload.privacyPolicy || "").trim();
    if (privacyPolicy) {
      body.privacyPolicy = privacyPolicy;
    }
  }

  return API.put(
    `${basePath}/${clientId}`,
    "tenant_client_update",
    body,
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantClientRoles = async (clientId, roles, tenantId) => {
  if (!clientId) {
    throw new Error("Client id is missing.");
  }

  return API.put(
    `${basePath}/${clientId}/role`,
    "tenant_client_update_roles",
    {
      roles: Array.isArray(roles)
        ? roles.map(String).map((role) => role.trim()).filter(Boolean)
        : [],
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const refreshTenantClientSecret = async (clientId, tenantId) => {
  if (!clientId) {
    throw new Error("Client id is missing.");
  }

  return API.put(
    `${basePath}/${clientId}/refreshclientsecret`,
    "tenant_client_refresh_secret",
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const updateClientEnabled = async (clientId, enabled, _accessToken, tenantId) => {
  if (!clientId) {
    throw new Error("Client id is missing.");
  }

  const action = enabled ? "enable" : "disable";
  return API.put(
    `${basePath}/${clientId}/${action}`,
    `tenant_client_${action}`,
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

export const tenantClientService = {
  getAllTenantClients,
  getTenantClientById,
  getTenantClientBasic,
  createTenantClient,
  updateTenantClient,
  updateClientEnabled,
  updateTenantClientRoles,
  refreshTenantClientSecret,
};
